import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useLang } from '../../contexts/LangContext';
import { useAuth } from '../../contexts/AuthContext';
import { selectBuilding } from '../../utils/buildingSelection';
import { settingsApi, QrSettings, VietQRBankOption } from '../../services/settingsApi';
import { resolveBackendAssetUrl } from '../../utils/apiBaseUrl';

interface BuildingOption {
  id: string;
  name: string;
}

interface QrSettingsPanelProps {
  settingsType: 'global' | 'user';
  setSettingsType: (type: 'global' | 'user') => void;
  selectedBuildingId: string;
  setSelectedBuildingId: (id: string) => void;
  buildings: BuildingOption[];
  isSuperAdmin: boolean;
}

export const QrSettingsPanel: React.FC<QrSettingsPanelProps> = ({
  settingsType,
  setSettingsType,
  selectedBuildingId,
  setSelectedBuildingId,
  buildings,
  isSuperAdmin,
}) => {
  const { t } = useLang();
  const { user } = useAuth();
  const [activeSubTab, setActiveSubTab] = useState<'payment' | 'app'>('payment');
  const [banks, setBanks] = useState<VietQRBankOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [sampleUrl, setSampleUrl] = useState<string | null>(null);
  const [form, setForm] = useState<QrSettings & {
    androidQrImageUrl?: string;
    iosQrImageUrl?: string;
  }>({
    paymentBankBin: '',
    paymentBankCode: '',
    paymentBankName: '',
    paymentAccount: '',
    paymentAccountName: '',
    shinhanVirtualAccountNumber: '',
    androidAppUrl: '',
    iosAppUrl: '',
  });
  const [hasDirectSettings, setHasDirectSettings] = useState(false);
  const [sampleAmount, setSampleAmount] = useState<string>('');
  const [sampleDescription, setSampleDescription] = useState<string>('');

  const isGlobal = settingsType === 'global' && isSuperAdmin;
  const canEdit = isGlobal || settingsType === 'user';

  const loadData = useCallback(async () => {
    try {
      const [bankList, settings] = await Promise.all([
        settingsApi.getQrBanks(),
        settingsApi.getQrSettings(
          isGlobal,
          isGlobal ? undefined : selectedBuildingId || undefined,
          { direct: true },
        ),
      ]);
      setBanks(bankList);
      setHasDirectSettings(Boolean(settings));
      if (settings) {
        setForm({
          paymentBankBin: settings.paymentBankBin || '',
          paymentBankCode: settings.paymentBankCode || '',
          paymentBankName: settings.paymentBankName || '',
          paymentAccount: settings.paymentAccount || '',
          paymentAccountName: settings.paymentAccountName || '',
          shinhanVirtualAccountNumber: settings.shinhanVirtualAccountNumber || '',
          androidAppUrl: settings.androidAppUrl || '',
          iosAppUrl: settings.iosAppUrl || '',
          androidQrImageUrl: settings.androidQrImageUrl || settings.androidQrImagePath,
          iosQrImageUrl: settings.iosQrImageUrl || settings.iosQrImagePath,
        });
      } else {
        setForm({
          paymentBankBin: '',
          paymentBankCode: '',
          paymentBankName: '',
          paymentAccount: '',
          paymentAccountName: '',
          shinhanVirtualAccountNumber: '',
          androidAppUrl: '',
          iosAppUrl: '',
          androidQrImageUrl: undefined,
          iosQrImageUrl: undefined,
        });
      }
    } catch (error) {
      console.error('Failed to load QR settings', error);
    }
  }, [isGlobal, selectedBuildingId, settingsType]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleBankChange = (bin: string) => {
    const bank = banks.find((b) => b.bin === bin);
    setForm((prev) => ({
      ...prev,
      paymentBankBin: bin,
      paymentBankCode: bank?.code || '',
      paymentBankName: bank?.name || '',
    }));
  };

  const buildSavePayload = () => {
    const { androidQrImageUrl, iosQrImageUrl, ...rest } = form;
    return {
      ...rest,
      shinhanVirtualAccountNumber: selectedBuildingId
        ? (rest.shinhanVirtualAccountNumber ?? '')
        : undefined,
      global: isGlobal,
      buildingId: isGlobal ? undefined : selectedBuildingId,
    };
  };

  const handleShinhanVaChange = (value: string) => {
    setForm((prev) => {
      const previousVa = prev.shinhanVirtualAccountNumber?.trim() || '';
      const next = { ...prev, shinhanVirtualAccountNumber: value };

      if (!value.trim()) {
        if (previousVa && prev.paymentAccount?.trim() === previousVa) {
          next.paymentAccount = '';
        }
        return next;
      }

      if (selectedBuildingId) {
        if (!prev.paymentBankBin || prev.paymentBankBin === '970424') {
          next.paymentBankBin = '970424';
          next.paymentBankCode = '970424';
          next.paymentBankName = 'Shinhan Bank Viet Nam';
        }
        if (!prev.paymentAccount || prev.paymentAccount.trim() === previousVa) {
          next.paymentAccount = value.trim();
        }
      }
      return next;
    });
  };

  const handleSavePayment = async () => {
    if (!canEdit) return;
    if (form.shinhanVirtualAccountNumber?.trim() && !selectedBuildingId && !isGlobal) {
      toast.error('Vui lòng chọn tòa nhà để lưu Số VA Shinhan');
      return;
    }

    const hasPaymentFields = Boolean(
      form.paymentBankBin && form.paymentAccount?.trim() && form.paymentAccountName?.trim(),
    );
    const canSaveVaOnly = Boolean(selectedBuildingId && !isGlobal);

    if (!hasPaymentFields && !canSaveVaOnly) {
      toast.error('Vui lòng chọn ngân hàng và nhập số tài khoản');
      return;
    }
    if (hasPaymentFields && !form.paymentAccountName?.trim()) {
      toast.error('Vui lòng nhập tên chủ tài khoản (bắt buộc cho mã QR ngân hàng)');
      return;
    }
    try {
      setLoading(true);
      await settingsApi.saveQrSettings(buildSavePayload());
      if (canSaveVaOnly && !form.shinhanVirtualAccountNumber?.trim()) {
        toast.success('Đã xóa Số VA Shinhan. Hệ thống sẽ dùng VA Prefix + mã dự án.');
      } else {
        toast.success(t('qrSavedSuccess'));
      }
      await loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save QR settings');
    } finally {
      setLoading(false);
    }
  };

  const hasPaymentOverride = Boolean(
    form.paymentBankBin ||
      form.paymentAccount?.trim() ||
      form.paymentAccountName?.trim() ||
      form.shinhanVirtualAccountNumber?.trim(),
  );

  const handleClearPayment = async () => {
    if (!canEdit) return;
    try {
      setLoading(true);
      await settingsApi.clearPaymentQrSettings({
        global: isGlobal,
        buildingId: isGlobal ? undefined : selectedBuildingId,
      });
      if (sampleUrl) URL.revokeObjectURL(sampleUrl);
      setSampleUrl(null);
      toast.success(t('qrPaymentClearedSuccess'));
      await loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to clear payment QR settings');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateSample = async () => {
    if (!canEdit) return;
    if (!form.paymentBankBin || !form.paymentAccount) {
      toast.error('Vui lòng chọn ngân hàng và nhập số tài khoản');
      return;
    }
    if (!form.paymentAccountName?.trim()) {
      toast.error('Vui lòng nhập tên chủ tài khoản (bắt buộc cho mã QR ngân hàng)');
      return;
    }
    try {
      setLoading(true);
      const blob = await settingsApi.generatePaymentQrSample({
        paymentBankBin: form.paymentBankBin,
        paymentAccount: form.paymentAccount,
        paymentAccountName: form.paymentAccountName,
        amount: sampleAmount ? Number(sampleAmount) : undefined,
        description: sampleDescription || undefined,
      });
      if (sampleUrl) URL.revokeObjectURL(sampleUrl);
      setSampleUrl(URL.createObjectURL(blob));
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to generate sample QR');
    } finally {
      setLoading(false);
    }
  };

  const applyAppQrResult = (result: QrSettings | null) => {
    if (result) {
      setHasDirectSettings(true);
      setForm((prev) => ({
        ...prev,
        androidAppUrl: result.androidAppUrl || '',
        iosAppUrl: result.iosAppUrl || '',
        androidQrImageUrl: result.androidQrImageUrl || result.androidQrImagePath,
        iosQrImageUrl: result.iosQrImageUrl || result.iosQrImagePath,
      }));
      return;
    }
    setHasDirectSettings(false);
    setForm((prev) => ({
      ...prev,
      androidAppUrl: '',
      iosAppUrl: '',
      androidQrImageUrl: undefined,
      iosQrImageUrl: undefined,
    }));
  };

  const handleSaveAppQr = async () => {
    if (!canEdit) return;
    try {
      setLoading(true);
      const result = await settingsApi.saveAppQrSettings({
        androidAppUrl: form.androidAppUrl,
        iosAppUrl: form.iosAppUrl,
        global: isGlobal,
        buildingId: isGlobal ? undefined : selectedBuildingId,
      });
      applyAppQrResult(result);
      const cleared = !form.androidAppUrl?.trim() && !form.iosAppUrl?.trim();
      toast.success(cleared ? t('qrAppClearedSuccess') : t('qrAppSavedSuccess'));
      await loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save app QR settings');
    } finally {
      setLoading(false);
    }
  };

  const handleClearAppQr = async () => {
    if (!canEdit) return;
    try {
      setLoading(true);
      const result = await settingsApi.saveAppQrSettings({
        androidAppUrl: '',
        iosAppUrl: '',
        global: isGlobal,
        buildingId: isGlobal ? undefined : selectedBuildingId,
      });
      applyAppQrResult(result);
      toast.success(t('qrAppClearedSuccess'));
      await loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to clear app QR settings');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAppQr = async () => {
    if (!canEdit) return;
    if (!form.androidAppUrl && !form.iosAppUrl) {
      toast.error('Vui lòng nhập ít nhất một link app');
      return;
    }
    try {
      setLoading(true);
      const result = await settingsApi.generateAppQrImages({
        androidAppUrl: form.androidAppUrl,
        iosAppUrl: form.iosAppUrl,
        global: isGlobal,
        buildingId: isGlobal ? undefined : selectedBuildingId,
      });
      applyAppQrResult(result);
      toast.success(t('qrAppGeneratedSuccess'));
      await loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to generate app QR');
    } finally {
      setLoading(false);
    }
  };

  const imageUrl = (path?: string) => resolveBackendAssetUrl(path);

  return (
    <div className="space-y-6">
      <div className="space-y-4 border-b border-gray-200 pb-4">
        <div className="flex space-x-4">
          {isSuperAdmin && (
            <button
              type="button"
              onClick={() => setSettingsType('global')}
              className={`px-4 py-2 rounded-md ${
                settingsType === 'global'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t('globalSettings')}
            </button>
          )}
          <button
            type="button"
            onClick={() => setSettingsType('user')}
            className={`px-4 py-2 rounded-md ${
              settingsType === 'user'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t('personalSettings')}
          </button>
        </div>

        {settingsType === 'user' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('selectBuildingForSettings')}
            </label>
            <select
              value={selectedBuildingId}
              onChange={(e) => selectBuilding(user?.id, e.target.value, setSelectedBuildingId)}
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">-- {t('selectBuildingForSettings')} (tất cả tòa nhà) --</option>
              {buildings.map((building) => (
                <option key={building.id} value={building.id}>
                  {building.name}
                </option>
              ))}
            </select>
            {!selectedBuildingId && (
              <p className="text-sm text-blue-600 mt-1">
                Không chọn tòa nhà: cài đặt áp dụng cho tất cả tòa nhà của bạn (trừ khi đã có cài đặt riêng theo tòa).
              </p>
            )}
          </div>
        )}

        {settingsType === 'global' && isSuperAdmin && (
          <p className="text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-md p-3">
            Cài đặt chung áp dụng cho tất cả chung cư khi chưa có cài đặt riêng theo tòa nhà.
          </p>
        )}
      </div>

      <div className="flex gap-2 border-b border-gray-200">
        {(['payment', 'app'] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveSubTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 ${
              activeSubTab === tab
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500'
            }`}
          >
            {tab === 'payment' ? t('qrPaymentTab') : t('qrAppTab')}
          </button>
        ))}
      </div>

      {activeSubTab === 'payment' && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
          {!isGlobal && !hasDirectSettings && (
            <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-md p-3">
              {t('qrPaymentUsingGlobalDefaults')}
            </p>
          )}
          {settingsType === 'user' && selectedBuildingId && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-4 space-y-2">
              <label className="block text-sm font-medium text-gray-800">
                Số VA Shinhan
              </label>
              <input
                type="text"
                value={form.shinhanVirtualAccountNumber || ''}
                onChange={(e) => handleShinhanVaChange(e.target.value)}
                disabled={!canEdit}
                placeholder="Số VA cố định do Shinhan cấp khi go-live"
                className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-50 font-mono"
              />
              <p className="text-sm text-amber-900">
                Nhập số VA Shinhan cấp cho chung cư này. Hệ thống sẽ dùng số này cho QR thanh toán,
                thông báo phí và app cư dân — không gọi API đăng ký VA.
                {' '}
                <span className="font-medium">Để trống và Lưu</span> nếu muốn dùng VA Prefix + mã dự án
                (đăng ký tự động qua API).
              </p>
            </div>
          )}

          {settingsType === 'user' && !selectedBuildingId && (
            <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-md p-3">
              Chọn tòa nhà ở trên để cấu hình Số VA Shinhan riêng cho từng chung cư.
            </p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('qrSelectBank')}</label>
              <select
                value={form.paymentBankBin || ''}
                onChange={(e) => handleBankChange(e.target.value)}
                disabled={!canEdit}
                className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-50"
              >
                <option value="">-- {t('qrSelectBank')} --</option>
                {banks.map((bank) => (
                  <option key={bank.bin} value={bank.bin}>
                    {bank.shortName} - {bank.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('qrBankAccount')}</label>
              <input
                type="text"
                value={form.paymentAccount || ''}
                onChange={(e) => setForm({ ...form, paymentAccount: e.target.value })}
                disabled={!canEdit}
                className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('qrAccountName')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.paymentAccountName || ''}
                onChange={(e) => setForm({ ...form, paymentAccountName: e.target.value })}
                disabled={!canEdit}
                placeholder="Tên chủ tài khoản đúng như trên ngân hàng"
                className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('qrAmountOptional')}</label>
              <input
                type="number"
                value={sampleAmount}
                onChange={(e) => setSampleAmount(e.target.value)}
                disabled={!canEdit}
                className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-50"
                placeholder="500000"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('qrDescriptionOptional')}</label>
              <input
                type="text"
                value={sampleDescription}
                onChange={(e) => setSampleDescription(e.target.value)}
                disabled={!canEdit}
                className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-50"
                placeholder="LINH TAY TOWER C1403 PQL T07 2026"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleSavePayment}
              disabled={loading || !canEdit}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {t('saveSettings')}
            </button>
            <button
              type="button"
              onClick={handleGenerateSample}
              disabled={loading || !canEdit}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50"
            >
              {t('qrGenerateSample')}
            </button>
            {hasPaymentOverride && (
              <button
                type="button"
                onClick={handleClearPayment}
                disabled={loading || !canEdit}
                className="bg-white text-red-600 border border-red-300 px-4 py-2 rounded-md hover:bg-red-50 disabled:opacity-50"
              >
                {t('qrClearPaymentQr')}
              </button>
            )}
          </div>

          {sampleUrl && (
            <div className="mt-4 p-4 border border-gray-200 rounded-lg inline-block">
              <p className="text-sm text-gray-600 mb-2">Sample QR (scan with your bank app to verify)</p>
              <img src={sampleUrl} alt="Payment QR sample" className="w-56 h-56" />
            </div>
          )}
        </div>
      )}

      {activeSubTab === 'app' && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
          {!isGlobal && !hasDirectSettings && (
            <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-md p-3">
              {t('qrAppUsingGlobalDefaults')}
            </p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('qrAndroidUrl')}</label>
              <input
                type="url"
                value={form.androidAppUrl || ''}
                onChange={(e) => setForm({ ...form, androidAppUrl: e.target.value })}
                disabled={!canEdit}
                className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-50"
                placeholder="https://play.google.com/store/apps/..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('qrIosUrl')}</label>
              <input
                type="url"
                value={form.iosAppUrl || ''}
                onChange={(e) => setForm({ ...form, iosAppUrl: e.target.value })}
                disabled={!canEdit}
                className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-50"
                placeholder="https://apps.apple.com/app/..."
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleSaveAppQr}
              disabled={loading || !canEdit}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {t('saveSettings')}
            </button>
            <button
              type="button"
              onClick={handleGenerateAppQr}
              disabled={loading || !canEdit || (!form.androidAppUrl?.trim() && !form.iosAppUrl?.trim())}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50"
            >
              {t('qrGenerateAppImages')}
            </button>
            {(hasDirectSettings && (form.androidAppUrl || form.iosAppUrl || form.androidQrImageUrl || form.iosQrImageUrl)) && (
              <button
                type="button"
                onClick={handleClearAppQr}
                disabled={loading || !canEdit}
                className="bg-white text-red-600 border border-red-300 px-4 py-2 rounded-md hover:bg-red-50 disabled:opacity-50"
              >
                {t('qrClearAppQr')}
              </button>
            )}
          </div>

          <div className="flex flex-wrap gap-6 mt-4">
            {form.androidQrImageUrl && (
              <div className="text-center">
                <img src={imageUrl(form.androidQrImageUrl)} alt="Android QR" className="w-40 h-40 mx-auto border" />
                <p className="text-sm mt-2">Android</p>
              </div>
            )}
            {form.iosQrImageUrl && (
              <div className="text-center">
                <img src={imageUrl(form.iosQrImageUrl)} alt="iOS QR" className="w-40 h-40 mx-auto border" />
                <p className="text-sm mt-2">iOS</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
